import { useCallback, useState } from 'react';

import { INSULATION_KIND } from '../../utils/insulationCatalog.js';
import {
  DEMAND_UNITS,
  SORT_ASC,
  SORT_COLUMNS,
  SORT_DESC,
  sanitizeDemandValue,
} from './insulationPickerView.js';

/**
 * Состояние «умного подбора утеплителя»: фильтры, сортировка, количества.
 *
 * Живёт НАД компонентом подбора, в «Товарах», а не внутри самой панели.
 * Причина: «Скрыть подбор» убирает панель с экрана, и вместе с ней
 * размонтировался бы весь набранный отбор — применения, толщины, диапазон
 * плотности, введённые количества. Менеджер, свернувший подбор чтобы взглянуть
 * на расчёт, возвращался бы к пустым фильтрам и набирал всё заново.
 *
 * Теперь скрытие только прячет панель. Отбор держится, пока открыт сам расчёт,
 * то есть до конца работы со сделкой.
 */
const useInsulationPickerState = () => {
  const [kind, setKind] = useState(INSULATION_KIND.MINERAL);
  const [applicationKeys, setApplicationKeys] = useState([]);
  const [thicknesses, setThicknesses] = useState([]);
  const [range, setRange] = useState({ from: '', to: '' });
  const [search, setSearch] = useState('');

  // Сводка «самое дешёвое по каждому применению» свёрнута по умолчанию: это
  // два десятка строк, и развёрнутой она отодвигает таблицу за экран. Открывают
  // её, когда применение ещё не выбрано и нужно понять, с чего начинать.
  //
  // Состояние живёт здесь, вместе с фильтрами, а не внутри самой сводки:
  // «Скрыть подбор» размонтирует панель, и развёрнутая сводка схлопывалась бы
  // обратно каждый раз, когда менеджер заглянул в расчёт и вернулся.
  const [digestOpen, setDigestOpen] = useState(false);
  const [sort, setSort] = useState(SORT_ASC);

  // Столбец сортировки. По умолчанию цена за м³: задание требует, чтобы самый
  // дешёвый товар был виден при любом наборе фильтров.
  const [sortColumn, setSortColumn] = useState(SORT_COLUMNS.PRICE_PER_M3);

  // Потребность одна на всю таблицу: менеджер считает объект целиком, а не
  // подбирает количество каждой позиции по отдельности.
  const [demand, setDemand] = useState({ value: '', unit: DEMAND_UNITS.PACK });

  // Смена вида материала обнуляет фильтры: плотность в кг/м³ и прочность в кПа
  // живут в разных шкалах, и перенесённый диапазон дал бы пустую таблицу.
  // Потребность при этом остаётся — она про объект, а не про фильтр.
  const changeKind = useCallback((nextKind) => {
    setKind(nextKind);
    setApplicationKeys([]);
    setThicknesses([]);
    setRange({ from: '', to: '' });
    // Поиск сбрасывается вместе с остальными: набранное «пеноплэкс» в минвате
    // даст пустую таблицу, и менеджер будет искать причину в фильтрах.
    setSearch('');
  }, []);

  // Сброс всех трёх фильтров разом. Потребность не трогаем: она про объект, а
  // не про отбор, и переживает любую смену фильтров.
  const resetFilters = useCallback(() => {
    setApplicationKeys([]);
    setThicknesses([]);
    setRange({ from: '', to: '' });
    setSearch('');
  }, []);

  const toggleApplication = useCallback((value) => setApplicationKeys((prev) => (
    prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
  )), []);

  const toggleThickness = useCallback((value) => setThicknesses((prev) => (
    prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
  )), []);

  const changeRange = useCallback((edge, value) => setRange((prev) => ({
    ...prev,
    [edge]: String(value).replace(/[^\d]/g, ''),
  })), []);

  /**
   * Клик по заголовку столбца.
   *
   * По тому же столбцу — переворот направления. По новому — переключение на
   * него по возрастанию: во всех трёх столбцах меньше значит дешевле или
   * меньше материала, и первый клик должен показывать лучшее, а не худшее.
   */
  const changeSort = useCallback((column) => {
    if (column === sortColumn) {
      setSort((prev) => (prev === SORT_ASC ? SORT_DESC : SORT_ASC));
      return;
    }

    setSortColumn(column);
    setSort(SORT_ASC);
  }, [sortColumn]);

  // Дробная потребность нормальна («12,5 м³»), поэтому запятая и точка
  // разрешены. Очистка живёт в модуле: её надо проверять тестом, а вставка из
  // буфера умеет приносить «1.2.3.4» разом.
  const changeDemandValue = useCallback((value) => setDemand((prev) => ({
    ...prev,
    value: sanitizeDemandValue(value),
  })), []);

  const changeDemandUnit = useCallback((unit) => setDemand((prev) => ({ ...prev, unit })), []);

  const changeSearch = useCallback((value) => setSearch(String(value || '')), []);

  const toggleDigest = useCallback(() => setDigestOpen((prev) => !prev), []);

  return {
    kind,
    applicationKeys,
    thicknesses,
    range,
    search,
    digestOpen,
    sort,
    sortColumn,
    demand,
    changeKind,
    resetFilters,
    toggleApplication,
    toggleThickness,
    changeRange,
    changeSearch,
    toggleDigest,
    changeSort,
    changeDemandValue,
    changeDemandUnit,
  };
};

export default useInsulationPickerState;
